import type { GameState, Unit } from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";
import { isImageReady } from "#/game/imageReady";
import {
  getSpriteUnitFrame,
  getSpriteUnitFrameSilhouette,
  getSpriteUnitShadowImage,
  hasUnitShadow,
  type SpriteUnitFrame,
} from "#/game/unitSprites";

import { isAnimationComplete } from "./boardAnimations";
import { TILE_SIZE } from "./boardConstants";
import type { UnitDrawLayout } from "./boardUnitGeometry";

export function shouldDrawUnitAsMoved({
  unit,
  currentPlayer,
  animation,
  nowMs,
}: {
  unit: Unit;
  currentPlayer: GameState["currentPlayer"];
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): boolean {
  if (unit.owner !== currentPlayer || !unit.hasMoved) return false;
  if (
    animation?.type === "move" &&
    animation.unitId === unit.id &&
    !isAnimationComplete(animation, nowMs)
  ) {
    return false;
  }
  return true;
}

function drawSpriteImage({
  ctx,
  source,
  layout,
  flipX,
  sx,
  sy,
  sw,
  sh,
  drawSize,
}: {
  ctx: CanvasRenderingContext2D;
  source: CanvasImageSource;
  layout: UnitDrawLayout;
  flipX: boolean;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  drawSize: number;
}): void {
  const drawX = layout.drawX + (TILE_SIZE - drawSize) / 2;
  const drawY = layout.drawY + (TILE_SIZE - drawSize) / 2;
  if (flipX) {
    ctx.translate(drawX + drawSize, drawY);
    ctx.scale(-1, 1);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, drawSize, drawSize);
    return;
  }
  ctx.drawImage(source, sx, sy, sw, sh, drawX, drawY, drawSize, drawSize);
}

function drawSpriteUnitHitFlash({
  ctx,
  frame,
  layout,
  hitFlash,
}: {
  ctx: CanvasRenderingContext2D;
  frame: SpriteUnitFrame;
  layout: UnitDrawLayout;
  hitFlash: number;
}): void {
  if (hitFlash <= 0) return;
  const whiteMask = getSpriteUnitFrameSilhouette(frame);
  if (!whiteMask) return;
  ctx.globalAlpha *= Math.min(1, hitFlash);

  drawSpriteImage({
    ctx,
    source: whiteMask,
    layout,
    flipX: frame.flipX,
    sx: 0,
    sy: 0,
    sw: whiteMask.width,
    sh: whiteMask.height,
    drawSize: frame.sw,
  });
}

function drawSpriteUnitShadow({
  ctx,
  kind,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  kind: Unit["kind"];
  layout: UnitDrawLayout;
}): void {
  if (!hasUnitShadow(kind)) return;
  const img = getSpriteUnitShadowImage(kind);
  if (!isImageReady(img)) return;
  drawSpriteImage({
    ctx,
    source: img,
    layout,
    flipX: false,
    sx: 0,
    sy: 0,
    sw: img.naturalWidth,
    sh: img.naturalHeight,
    drawSize: img.naturalWidth,
  });
}

function drawSpriteUnitFrame({
  ctx,
  frame,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  frame: SpriteUnitFrame;
  layout: UnitDrawLayout;
}): void {
  drawSpriteImage({
    ctx,
    source: frame.img,
    layout,
    flipX: frame.flipX,
    sx: frame.sx,
    sy: frame.sy,
    sw: frame.sw,
    sh: frame.sh,
    drawSize: frame.sw,
  });
}

export function drawSpriteUnit({
  ctx,
  unit,
  state,
  layout,
  hitFlash,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  state: GameState;
  layout: UnitDrawLayout;
  hitFlash: number;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  const moved = shouldDrawUnitAsMoved({
    unit,
    currentPlayer: state.currentPlayer,
    animation,
    nowMs,
  });
  const frame = getSpriteUnitFrame({
    kind: unit.kind,
    owner: unit.owner,
    facing: layout.facing,
    moved,
  });
  if (!frame) return;

  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.save();
  drawSpriteUnitShadow({ ctx, kind: unit.kind, layout });
  drawSpriteUnitFrame({ ctx, frame, layout });
  ctx.restore();
  ctx.save();
  drawSpriteUnitHitFlash({ ctx, frame, layout, hitFlash });
  ctx.restore();
  ctx.restore();
}
